import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	InputLabel,
	TextField,
	Typography
} from "@mui/material";
import React, { FC, useEffect, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import { useGetSiteConfigQuery, useGetSocietyConfigQuery } from "../../redux/reducers/settings.reducer";
import { useEditBuildingConfigurationMutation } from "../../redux/reducers/configuration.reducer";
import { ISocietyDetails } from "../../interfaces";
import { useAppSelector } from "../../redux";

interface IEditSocietyDialogProps {
	open: boolean;
	onClose: () => void;
}

interface IEditSocietyInputs {
	name: string;
}

const EditSocietyDialog: FC<IEditSocietyDialogProps> = (props) => {
	const { open, onClose } = props;

	const authState = useAppSelector((state) => state.auth);

	// APIS
	// GET SITE CONFIG
	const { data: siteConfig } = useGetSiteConfigQuery(null, {
		refetchOnMountOrArgChange: true,
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	// GET SOCIETY CONFIG
	const { data: getSocietyConfigResponse, refetch: refetchSocietyConfig } = useGetSocietyConfigQuery(null, {
		skip: !siteConfig
	});

	const societyDetails = useMemo<ISocietyDetails | null>(() => {
		if (getSocietyConfigResponse && getSocietyConfigResponse.results.length > 0) {
			return getSocietyConfigResponse.results[0];
		}

		return null;
	}, [getSocietyConfigResponse]);

	// EDIT BUILDING CONFIGURATION
	const [
		editSociety,
		{
			isLoading: editSocietyLoading,
			error: editSocietyError,
			isSuccess: editSocietySuccess,
			reset: resetEditSocietyState
		}
	] = useEditBuildingConfigurationMutation();

	const {
		reset,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<IEditSocietyInputs>();

	function handleSubmitForm(formData: IEditSocietyInputs) {
		if (societyDetails) {
			editSociety({
				id: societyDetails.id,
				data: { name: formData.name }
			});
		}
	}

	useEffect(() => {
		if (open && societyDetails) {
			reset({ name: societyDetails.name });
		} else {
			reset({ name: "" });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getSocietyConfigResponse, open]);

	useEffect(() => {
		if (editSocietySuccess) {
			onClose();
			resetEditSocietyState();
			refetchSocietyConfig();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editSocietySuccess]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
			<DialogTitle textAlign="center" classes={{ root: `dialog-title-root primary` }}>
				Edit Society Details
				<Box className="close-dialog-icon-wrapper">
					<IconButton color="inherit" onClick={onClose}>
						<CloseIcon color="inherit" />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent classes={{ root: "dialog-content-root" }}>
				<Box component="form" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<InputLabel
								required
								htmlFor="society-name-input"
								error={!!errors.name}
								sx={{ fontWeight: 500, marginBottom: "0.5rem" }}
							>
								Society Name
							</InputLabel>

							<TextField
								required
								fullWidth
								size="small"
								variant="outlined"
								color="primary"
								id="society-name-input"
								placeholder="Enter society name"
								error={!!errors.name}
								helperText={errors.name && errors.name.message ? errors.name.message.toString() : ""}
								{...register("name", { required: "Society name is required" })}
							/>
						</Grid>

						{editSocietyError ? (
							<Grid item xs={12}>
								<Typography variant="body2" color="var(--color-error-main)">
									{"data" in editSocietyError && editSocietyError.data
										? String(editSocietyError.data)
										: "Failed to edit society"}
								</Typography>
							</Grid>
						) : null}

						<Grid item xs={12}>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
								<LoadingButton
									disableElevation
									type="submit"
									color="success"
									variant="contained"
									loading={editSocietyLoading}
								>
									Save
								</LoadingButton>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default EditSocietyDialog;
